import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text, TextArea } from '@viapeople/components'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { DEFAULT_COLORS } from './constants'

/**
 * @fileoverview PEC Survey Page
 *
 * Page for participants to submit their peer evaluation responses
 * Supports custom rating scales, N/A options, and optional text feedback
 */

interface RatingOption {
	value: string
	valueLabel: string
	labelColor?: string
}

interface ResponseData {
	questionId: string
	revieweeId: string
	ratingResponse: number | string | null
	textResponse: string
}

export const PecSurveyPage = () => {
	const { pecCycleId } = useParams<{ pecCycleId: string }>()
	const [searchParams] = useSearchParams()
	const participantIdParam = searchParams.get('participantId')
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const toast = useToast()
	const { user } = sessionStore()

	// Use the participantId from URL param or fall back to logged-in user
	const participantId = participantIdParam || user?.id
	const organizationId = user?.organizationId

	const [responses, setResponses] = useState<Record<string, ResponseData>>({})
	const [isSubmitted, setIsSubmitted] = useState(false)

	// Fetch PEC cycle details
	const { data: cycle, isLoading: isLoadingCycle } = useQuery({
		...trpc.client.pec.getById.queryOptions({
			id: pecCycleId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!pecCycleId && !!organizationId,
	})

	// Fetch participant's questions/responses
	const { data: participantData, isLoading: isLoadingResponses } = useQuery({
		...trpc.client.pec.getParticipantResponses.queryOptions({
			pecCycleId: pecCycleId as string,
			participantId: participantId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!pecCycleId && !!participantId && !!organizationId,
	})

	const submitMutation = useMutation({
		...trpc.client.pec.batchSubmitResponses.mutationOptions(),
		onSuccess: () => {
			setIsSubmitted(true)
			queryClient.invalidateQueries({
				queryKey: trpc.client.pec.getParticipantResponses.queryKey(),
			})
		},
		onError: (error) => {
			const message = error instanceof Error ? error.message : 'Failed to submit responses. Please try again.'
			toast.showToast(message, 'error')
		},
	})

	const handleRatingChange = (questionId: string, revieweeId: string, rating: number | string | null) => {
		setResponses((prev) => ({
			...prev,
			[questionId]: {
				...prev[questionId],
				questionId,
				revieweeId,
				ratingResponse: rating,
				textResponse: prev[questionId]?.textResponse || '',
			},
		}))
	}

	const handleFeedbackChange = (questionId: string, revieweeId: string, text: string) => {
		setResponses((prev) => ({
			...prev,
			[questionId]: {
				...prev[questionId],
				questionId,
				revieweeId,
				ratingResponse: prev[questionId]?.ratingResponse ?? null,
				textResponse: text,
			},
		}))
	}

	const handleSubmit = async () => {
		// Filter responses that have a rating (including N/A which is null)
		const responsesToSubmit = Object.values(responses).filter(
			(r) => r.ratingResponse !== undefined && r.ratingResponse !== 0,
		)

		if (responsesToSubmit.length === 0) {
			toast.showToast('Please provide at least one rating before submitting', 'error')
			return
		}

		await submitMutation.mutateAsync({
			pecCycleId: pecCycleId as string,
			participantId: participantId as string,
			responses: responsesToSubmit,
		})
	}

	const handleBackToDashboard = () => {
		navigate('/dashboard')
	}

	const isLoading = isLoadingCycle || isLoadingResponses

	if (isLoading) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<SpinnerWithBackground />
			</div>
		)
	}

	if (!cycle) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div
					className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'
					data-testid='pec-survey-not-found'>
					<Text variant='large' weight='semibold' className='text-red-600 mb-4'>
						Survey Not Found
					</Text>
					<Text variant='small' className='text-gray-600 mb-4'>
						This PEC survey is no longer available or you don't have access to it.
					</Text>
					<Button variant='secondary' onClick={handleBackToDashboard}>
						Back to Dashboard
					</Button>
				</div>
			</div>
		)
	}

	if (cycle.status !== 'active') {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div
					className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'
					data-testid='pec-survey-inactive'>
					<Text variant='large' weight='semibold' className='text-amber-600 mb-4'>
						Survey Not Active
					</Text>
					<Text variant='small' className='text-gray-600 mb-4'>
						This PEC cycle is currently {cycle.status}. Responses can only be submitted when the cycle is active.
					</Text>
					<Button variant='secondary' onClick={handleBackToDashboard}>
						Back to Dashboard
					</Button>
				</div>
			</div>
		)
	}

	if (isSubmitted) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div
					className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'
					data-testid='pec-success-message'>
					<div className='mb-6'>
						<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<svg
								className='w-8 h-8 text-green-600'
								fill='none'
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth='2'
								viewBox='0 0 24 24'
								stroke='currentColor'
								aria-hidden='true'>
								<path d='M5 13l4 4L19 7'></path>
							</svg>
						</div>
						<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
							Thank You!
						</Text>
						<Text variant='small' className='text-gray-600'>
							Your peer evaluations have been submitted successfully.
						</Text>
					</div>
					<Button variant='primary' onClick={handleBackToDashboard}>
						Back to Dashboard
					</Button>
				</div>
			</div>
		)
	}

	const questions = participantData?.questions || []
	const stats = participantData?.stats

	// Get rating options from custom config or generate from ratingScale
	const ratingOptions: RatingOption[] =
		cycle.ratingConfig?.options ||
		Array.from({ length: cycle.ratingScale || 5 }, (_, i) => ({
			value: String(i + 1),
			valueLabel: String(i + 1),
			labelColor: DEFAULT_COLORS[i] || '#2563EB',
		}))

	const allowNa = cycle.allowNa ?? false
	const textFeedbackEnabled = cycle.textFeedbackEnabled ?? true

	return (
		<div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8'>
			<div className='max-w-4xl mx-auto'>
				<div className='bg-white rounded-lg shadow-xl overflow-hidden'>
					{/* Header */}
					<div className='bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6' data-testid='pec-survey-header'>
						<Text variant='large' weight='semibold' className='text-white mb-2 text-2xl'>
							{cycle.name}
						</Text>
						<Text variant='small' className='text-blue-100'>
							Peer Evaluation Survey
						</Text>
						{stats && (
							<div className='mt-4 flex gap-4' data-testid='pec-survey-stats'>
								<div className='bg-white/20 rounded-lg px-4 py-2'>
									<Text variant='xsmall' className='text-blue-100'>
										Completed
									</Text>
									<Text variant='small' weight='semibold' className='text-white'>
										{stats.completedQuestions} / {stats.totalQuestions}
									</Text>
								</div>
								<div className='bg-white/20 rounded-lg px-4 py-2'>
									<Text variant='xsmall' className='text-blue-100'>
										Progress
									</Text>
									<Text variant='small' weight='semibold' className='text-white'>
										{Math.round(stats.completionRate)}%
									</Text>
								</div>
							</div>
						)}
					</div>

					{/* Prompt */}
					{cycle.promptText && (
						<div className='px-8 py-4 bg-blue-50 border-b border-blue-100' data-testid='pec-survey-prompt'>
							<Text variant='small' weight='semibold' className='text-blue-900' style={{ whiteSpace: 'pre-line' }}>
								{cycle.promptText}
							</Text>
						</div>
					)}

					{/* Questions */}
					<div className='px-8 py-8 space-y-8'>
						{questions.length === 0 ? (
							<div className='text-center py-8'>
								<Text variant='small' className='text-gray-600'>
									No evaluations assigned to you for this cycle.
								</Text>
							</div>
						) : (
							questions.map((question) => {
								const existingRating = question.ratingResponse ?? responses[question.questionId]?.ratingResponse
								const existingText = question.textResponse || responses[question.questionId]?.textResponse || ''
								const isCompleted = question.status === 'completed'
								const isNaSelected = existingRating === null

								return (
									<div
										key={question.questionId}
										className={`border rounded-lg p-6 ${isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}
										data-testid={`pec-participant-${question.revieweeId}`}>
										<div className='flex items-center justify-between mb-4'>
											<Text variant='medium' weight='semibold' className='text-gray-900'>
												{question.revieweeFirstName} {question.revieweeLastName}
											</Text>
											{isCompleted && (
												<span className='px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full'>Completed</span>
											)}
										</div>

										{/* Rating Scale */}
										<div className='mb-4'>
											<Text variant='xsmall' className='text-gray-600 mb-2 block'>
												Rating
											</Text>
											<div className='flex gap-2 flex-wrap items-start'>
												{ratingOptions.map((option) => {
													const isSelected = String(existingRating) === option.value
													return (
														<div key={option.value} className='text-center'>
															<button
																type='button'
																onClick={() =>
																	handleRatingChange(question.questionId, question.revieweeId, option.value)
																}
																className='w-12 h-12 rounded-lg font-semibold transition-all'
																style={{
																	backgroundColor: isSelected ? option.labelColor || '#2563EB' : '#F3F4F6',
																	color: isSelected ? 'white' : '#374151',
																	transform: isSelected ? 'scale(1.1)' : 'scale(1)',
																}}
																data-testid={`pec-rating-${option.value}`}>
																{option.value}
															</button>
															{option.valueLabel !== option.value && (
																<Text
																	variant='xsmall'
																	className='mt-1 max-w-[60px] truncate block'
																	style={{ color: option.labelColor || '#6B7280' }}>
																	{option.valueLabel}
																</Text>
															)}
														</div>
													)
												})}
												{/* N/A Button */}
												{allowNa && (
													<div className='text-center'>
														<button
															type='button'
															onClick={() => handleRatingChange(question.questionId, question.revieweeId, null)}
															className={`w-12 h-12 rounded-lg font-semibold transition-all ${
																isNaSelected
																	? 'bg-neutral-600 text-white scale-110'
																	: 'bg-neutral-200 hover:bg-neutral-300 text-neutral-600'
															}`}
															data-testid='pec-rating-na'>
															N/A
														</button>
													</div>
												)}
											</div>
										</div>

										{/* Feedback - only show if enabled */}
										{textFeedbackEnabled && (
											<div>
												<Text variant='xsmall' className='text-gray-600 mb-2 block'>
													Additional feedback (optional)
												</Text>
												<TextArea
													value={existingText}
													onChange={(e) =>
														handleFeedbackChange(question.questionId, question.revieweeId, e.target.value)
													}
													placeholder='Share any additional feedback...'
													rows={3}
													data-testid='pec-feedback-textarea'
												/>
											</div>
										)}
									</div>
								)
							})
						)}

						{/* Action Buttons */}
						{questions.length > 0 && (
							<div className='flex justify-between pt-4 border-t'>
								<Button variant='secondary' onClick={handleBackToDashboard} data-testid='pec-save-exit-button'>
									Save & Exit
								</Button>
								<Button
									variant='primary'
									onClick={handleSubmit}
									disabled={Object.keys(responses).length === 0 || submitMutation.isPending}
									data-testid='pec-submit-button'>
									{submitMutation.isPending ? 'Submitting...' : 'Submit All Responses'}
								</Button>
							</div>
						)}
					</div>

					{/* Footer */}
					<div className='bg-gray-50 px-8 py-4 border-t border-gray-200'>
						<Text variant='xsmall' className='text-gray-600 text-center'>
							Your responses are anonymous and will be aggregated for analysis
						</Text>
					</div>
				</div>
			</div>
		</div>
	)
}
